<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import IconButton from '@/components/shared/IconButton.vue'
import iconPath from '@/assets/images/logo.svg'
import {
	PermAdminCustomFields,
	PermAdminInstances,
	PermAdminJira,
	PermOwnRiskRead,
	PermAllRiskRead,
	PermAdminStandardUpgrade,
	PermAdminUsers,
	PermAllAssetRead,
	PermAllBPRead,
	PermAllDataAssetRead,
	PermAllMeasureRead,
	PermAllModuleRead,
	PermOwnAssetRead,
	PermOwnBPRead,
	PermOwnDataAssetRead,
	PermOwnMeasureRead,
	PermOwnModuleRead,
	PermDocumentRead,
} from '@/models/enums-generated'
import { hasPermission, viewing } from '@/services/utils'

const { t } = useI18n()
const isUpgradeAvailable = computed(() => sessionMap.isUpgradeAvailable && hasPermission(PermAdminStandardUpgrade))

const activeButton = computed(() => {
	return (
		buttons.find((b) => {
			if (b.link === './app/bp' && window.location.pathname.startsWith('/app/bp')) {
				return true
			}
			return b.link.replaceAll('./', '/') === window.location.pathname
		})?.name || ''
	)
})

const buttons = [
	{ id: 'sidenav-dashboard', name: 'dashboard', icon: 'bi-pie-chart', title: t('dashboard.label'), link: './app' },
	{
		id: 'sidenav-bps',
		name: 'business-processes',
		icon: 'bi-kanban',
		title: t('businessProcesses'),
		link: './app/bp',
		condition: hasPermission(PermAllBPRead, PermOwnBPRead),
	},
	{
		id: 'sidenav-assets',
		name: 'assets',
		icon: 'bi-clipboard',
		title: t('assets'),
		link: './app/assets',
		condition: hasPermission(PermAllAssetRead, PermOwnAssetRead),
	},
	{
		id: 'sidenav-data-assets',
		name: 'data-assets',
		icon: 'bi-database',
		title: t('dataAssets'),
		link: './app/dataassets',
		condition: hasPermission(PermAllDataAssetRead, PermOwnDataAssetRead),
	},
	{
		id: 'sidenav-measures',
		name: 'measures',
		icon: 'bi-shield-lock',
		title: t('measure', 2),
		link: './app/methods',
		condition: hasPermission(PermAllMeasureRead, PermOwnMeasureRead),
	},
	{
		id: 'sidenav-modules',
		name: 'modules',
		icon: 'bi-layers',
		title: t('module.label', 2),
		link: './app/modules',
		condition: hasPermission(PermAllModuleRead, PermOwnModuleRead),
	},
	{
		id: 'sidenav-users',
		name: 'users',
		icon: 'bi-people',
		title: t('user.label', hasPermission(PermAdminUsers) ? 2 : 1),
		link: './app/users',
	},
	{
		id: 'sidenav-institutions',
		name: 'institutions',
		icon: 'bi-building',
		title: t('institution', 2),
		link: './app/instances',
		condition: hasPermission(PermAdminInstances),
	},
	{
		id: 'sidenav-risk-management',
		name: 'risk-management',
		icon: 'bi-exclamation-triangle',
		title: t('risk.riskManagement'),
		link: './app/risk-management',
		condition: hasPermission(PermOwnRiskRead, PermAllRiskRead),
	},
	{
		id: 'sidenav-documents',
		name: 'documents',
		icon: 'bi-folder2',
		title: t('document.label', 2),
		link: './app/documents',
		condition: hasPermission(PermDocumentRead),
	},
	{
		id: 'sidenav-settings',
		name: 'settings',
		icon: 'bi-gear',
		title: t('settings.label'),
		link: './app/settings',
		condition: hasPermission(PermAdminJira, PermAdminCustomFields),
	},
]
</script>

<template>
	<div class="sidebar-wrapper">
		<div class="col-sm-auto bg-side sticky-top sidebar">
			<div class="sidebar-container">
				<a href="./app" class="d-block p-3 link-dark text-decoration-none" v-one-tooltip="'EITS'" data-test="sidenav-cybsis-logo">
					<img :src="iconPath" class="logo-img" />
				</a>
				<ul class="nav-list">
					<template v-for="button in buttons" :key="button.name">
						<li v-if="button.condition !== false" class="mb-3">
							<div class="button-container" :class="{ active: activeButton === button.name }">
								<div class="indicator"></div>
								<IconButton
									:data-test="button.id"
									:href="button.link"
									:title="button.title"
									:class="`bi ${button.icon} nav-icon`"
									tooltipPlacement="right"
								/>
							</div>
						</li>
					</template>
				</ul>

				<template v-if="isUpgradeAvailable && viewing">
					<div class="mb-3 ms-1">
						<i
							id="upgrade-eits-btn"
							class="blob bi bi-info-circle-fill fs-2 text-primary upgrade-btn"
							v-one-tooltip="t('standardVersionUpdateAvailable')"
							data-test="nav-standard-update"
						></i>
					</div>
				</template>

				<div id="logout-btn" class="fs-3 pt-4 pb-5 ms-3">
					<IconButton
						href="./logout"
						:title="t('logout')"
						class="bi bi-door-open log-out"
						tooltipPlacement="right"
						data-test="nav-logout"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped>
@import ~bootstrap/scss/functions
@import ~bootstrap/scss/variables

.sidebar-wrapper
  background-color: var(--bs-body-bg)
  position: relative
  width: 96px

.sidebar
  width: 96px
  position: fixed
  top: 0
  left: 0
  bottom: 0
  z-index: 1030
  background-color: var(--bs-body-bg)

.sidebar-container
  display: flex
  flex-direction: column
  align-items: center
  position: fixed
  height: 100vh
  overflow-y: auto
  top: 0
  left: 0
  z-index: 1030

.logo-img
  width: 40px

.indicator
  position: absolute
  top: 50%
  left: -5px
  transform: translateY(-50%)
  width: 5px
  height: 40px
  background-color: $primary
  opacity: 0
  transition: opacity 0.3s ease

.nav-list
  display: flex
  flex-direction: column
  align-items: center
  padding: 0
  list-style: none
  margin-bottom: auto
  &:hover .button-container.active .indicator
    opacity: 0

.button-container
  width: 96px
  position: relative
  display: flex
  justify-content: center
  align-items: center
  transition: background-color 0.3s ease
  border-left: 5px solid transparent
  &:hover .indicator
    opacity: 1 !important

.button-container.active .indicator
  opacity: 1

.nav-icon
  font-size: 1.5rem
  transition: transform 0.2s ease

.button-container.active .nav-icon
  color: var(--bs-primary)
  transform: scale(1.2)

.button-container:hover .nav-icon
  color: var(--bs-primary)
  transform: scale(1.2)

.log-out:hover
    color: var(--bs-danger)

.log-out::after
  content: "\F135"
  font-family: "bootstrap-icons"
  display: inline-block
  margin-left: -10px
  vertical-align: -2px
  font-size: 0.9em

@keyframes pulse
  0%
    transform: scale(1)
    opacity: 1
  50%
    transform: scale(1.1)
    opacity: 0.75
  100%
    transform: scale(1)
    opacity: 1

.upgrade-btn
  cursor: pointer
  animation: pulse 1.5s infinite ease-in-out
  transition: transform 0.3s ease
</style>
